import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import type {
    Coupon,
    InsertCoupon,
    UpdateCoupon,
    Campaign,
    InsertCampaign,
    UpdateCampaign,
    CouponRule,
    InsertCouponRule,
    ValidateCouponRequest,
    ValidateCouponResponse,
    CouponAnalytics,
    CouponQueryParams,
} from '@baboor/baboor-models'
import { ApiResponse } from '@baboor/common-models'
import { toHttpParams } from '@baboor/common-util'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class DiscountsApiService {
    private apiUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.apiUrl = `${env.apiUrl}/v1/discounts`
    }

    getCoupons(params?: CouponQueryParams): Observable<ApiResponse<Coupon[]>> {
        return this.http.get<ApiResponse<Coupon[]>>(`${this.apiUrl}/coupons`, {
            params: toHttpParams(params ?? {}),
        })
    }

    getCoupon(id: string): Observable<ApiResponse<Coupon>> {
        return this.http.get<ApiResponse<Coupon>>(
            `${this.apiUrl}/coupons/${id}`,
        )
    }

    createCoupon(data: InsertCoupon): Observable<ApiResponse<Coupon>> {
        return this.http.post<ApiResponse<Coupon>>(
            `${this.apiUrl}/coupons`,
            data,
        )
    }

    updateCoupon(
        id: string,
        data: UpdateCoupon,
    ): Observable<ApiResponse<Coupon>> {
        return this.http.patch<ApiResponse<Coupon>>(
            `${this.apiUrl}/coupons/${id}`,
            data,
        )
    }

    deleteCoupon(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/coupons/${id}`,
        )
    }

    deactivateCoupon(id: string): Observable<ApiResponse<Coupon>> {
        return this.http.patch<ApiResponse<Coupon>>(
            `${this.apiUrl}/coupons/${id}/deactivate`,
            {},
        )
    }

    createCouponRule(
        data: InsertCouponRule,
    ): Observable<ApiResponse<CouponRule>> {
        return this.http.post<ApiResponse<CouponRule>>(
            `${this.apiUrl}/coupon-rules`,
            data,
        )
    }

    deleteCouponRule(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/coupon-rules/${id}`,
        )
    }

    validateCoupon(
        data: ValidateCouponRequest,
    ): Observable<ApiResponse<ValidateCouponResponse>> {
        return this.http.post<ApiResponse<ValidateCouponResponse>>(
            `${this.apiUrl}/coupons/validate`,
            data,
        )
    }

    getCampaigns(): Observable<ApiResponse<Campaign[]>> {
        return this.http.get<ApiResponse<Campaign[]>>(
            `${this.apiUrl}/campaigns`,
        )
    }

    createCampaign(data: InsertCampaign): Observable<ApiResponse<Campaign>> {
        return this.http.post<ApiResponse<Campaign>>(
            `${this.apiUrl}/campaigns`,
            data,
        )
    }

    updateCampaign(
        id: string,
        data: UpdateCampaign,
    ): Observable<ApiResponse<Campaign>> {
        return this.http.patch<ApiResponse<Campaign>>(
            `${this.apiUrl}/campaigns/${id}`,
            data,
        )
    }

    deleteCampaign(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/campaigns/${id}`,
        )
    }

    getCouponAnalytics(): Observable<ApiResponse<CouponAnalytics>> {
        return this.http.get<ApiResponse<CouponAnalytics>>(
            `${this.apiUrl}/analytics`,
        )
    }
}
